import React from 'react'
import { Box } from '@mui/material'
import { Image, type ImageData } from '@media-components'
import {
  SectionHeader,
  SectionWrapper,
  type SectionHeaderData,
} from '@components/common'
import styles from './styles'

export type ImageGallerySectionData = {
  imagesData: ImageData[]
  sectionHeaderData?: SectionHeaderData
  darkBg?: boolean
}

type ImageGallerySectionProps = ImageGallerySectionData & {
  showDivider?: 'top' | 'bottom' | 'both' | 'none'
  alternateSectionBg?: boolean
}

/**
 * Calculate which row an image belongs to based on alternating 4-3 pattern
 * Row 0: 4 images (indices 0-3)
 * Row 1: 3 images (indices 4-6)
 * Row 2: 4 images (indices 7-10)
 * Row 3: 3 images (indices 11-13)
 * ... and so on
 */
const getRowInfo = (index: number) => {
  // Each cycle has 7 images (4 + 3)
  const cycleSize = 7
  const cycle = Math.floor(index / cycleSize)
  const positionInCycle = index % cycleSize

  // Within each cycle:
  // - First 4 images (0-3) belong to an even row
  // - Next 3 images (4-6) belong to an odd row
  const isFirstPartOfCycle = positionInCycle < 4
  const rowInCycle = isFirstPartOfCycle ? 0 : 1
  const rowIndex = cycle * 2 + rowInCycle

  // Calculate the starting index for this row
  const rowStartIndex = isFirstPartOfCycle
    ? cycle * cycleSize // Start of even row in this cycle
    : cycle * cycleSize + 4 // Start of odd row in this cycle

  // Odd rows have 3 images, even rows have 4
  const isOddRow = rowIndex % 2 === 1
  const imagesInRow = isOddRow ? 3 : 4

  return {
    rowIndex,
    rowStartIndex,
    isOddRow,
    imagesInRow,
    positionInRow: index - rowStartIndex,
  }
}

export function ImageGallerySection({
  imagesData,
  sectionHeaderData,
  showDivider,
  darkBg = false,
  alternateSectionBg = false,
}: ImageGallerySectionProps) {
  return (
    <SectionWrapper
      alternateBg={darkBg}
      showDivider={showDivider}
      alternateSectionBg={alternateSectionBg}
    >
      {sectionHeaderData && (
        <SectionHeader {...sectionHeaderData} darkBg={darkBg} />
      )}

      <Box sx={styles.collageWrapper}>
        {imagesData.map((imgInfo, index) => {
          const { isOddRow, imagesInRow, positionInRow } = getRowInfo(index)

          // Grid column span: 12 columns total
          // Even rows (4 images): each spans 3 columns (12/4 = 3)
          // Odd rows (3 images): each spans 4 columns (12/3 = 4)
          const gridColumnSpan = isOddRow ? 4 : 3

          // Width based on row type
          const imageWidth = isOddRow
            ? {
                xs: '100%',
                // sm: 'calc(100% / 3)',
                lg: '456px',
              }
            : {
                xs: '100%',
                // sm: 'calc(100% / 4)',
                lg: '333px',
              }

          return (
            <Box
              key={`${imgInfo.src}-${index}`}
              sx={{
                gridColumn: {
                  xs: 'auto',
                  sm: `span ${gridColumnSpan}`,
                },
                position: 'relative',
                height: '215px',
                width: imageWidth,
                // Center odd rows (3 images)
                marginLeft:
                  isOddRow && positionInRow === 0
                    ? { xs: '0', sm: 'auto' }
                    : '0',
                marginRight:
                  isOddRow && positionInRow === imagesInRow - 1
                    ? { xs: '0', sm: 'auto' }
                    : '0',
                justifySelf: isOddRow ? 'center' : 'auto',
              }}
            >
              <Image
                {...imgInfo}
                style={{
                  borderRadius: '8px',
                  width: '100%',
                  height: '100%',
                }}
              />
            </Box>
          )
        })}
      </Box>
    </SectionWrapper>
  )
}
